import React, { useMemo } from "react";
import { Bar } from "@visx/shape";
import { Group } from "@visx/group";
import { AxisLeft } from "@visx/axis";
import { scaleLinear, scaleBand, scaleOrdinal } from "@visx/scale";
import { Text } from "@visx/text";
import { useChartDimensions } from "../../../../hooks/useChartDimensions";

const HorizontalStackedBarChart = ({
  data,
  width = 700,
  height = 400,
  margin = { top: 40, right: 100, bottom: 40, left: 150 },
  colors = [
    "#157787",
    "#A1C9CF",
    "#2ca02c",
    "#d62728",
    "#9467bd",
    "#8c564b",
    "#e377c2",
    "#7f7f7f",
    "#bcbd22",
    "#17becf",
  ],
  className = "",
  showLegend = true,
  showPercentages = false,
  title,
  description,
  legendHeight = 50,
}) => {
  // Adjust margins if legend is displayed
  const adjustedMargin = showLegend
    ? { ...margin, bottom: margin.bottom + legendHeight }
    : margin;

  // Use custom hook for responsive chart dimensions
  const [containerRef, dimensions] = useChartDimensions({
    width,
    height,
    marginTop: adjustedMargin.top,
    marginRight: adjustedMargin.right,
    marginBottom: adjustedMargin.bottom,
    marginLeft: adjustedMargin.left,
  });

  const { boundedWidth, boundedHeight } = dimensions;

  // Helper function to extract value from data item
  const getValue = (item) => {
    return typeof item === "number" ? item : item.value;
  };

  // Get all categories (y-axis)
  const categories = useMemo(() => Object.keys(data), [data]);

  // Get all segments (stacks in the bar)
  const segments = useMemo(() => {
    if (categories.length > 0) {
      return Object.keys(data[categories[0]]);
    }
    return [];
  }, [data, categories]);

  // Create color scale for segments
  const colorScale = useMemo(
    () =>
      scaleOrdinal({
        domain: segments,
        range: colors.slice(0, segments.length),
      }),
    [segments, colors]
  );

  // Create y scale for categories (vertical axis)
  const yScale = useMemo(
    () =>
      scaleBand({
        domain: categories,
        range: [0, boundedHeight],
        padding: 0.2,
      }),
    [categories, boundedHeight]
  );

  // Create x scale for values (horizontal axis)
  const xScale = useMemo(() => {
    const maxTotal = Math.max(
      ...categories.map((category) =>
        segments.reduce((sum, segment) => {
          const value = getValue(data[category][segment] || 0);
          return sum + value;
        }, 0)
      )
    );
    return scaleLinear({
      domain: [0, maxTotal],
      range: [0, boundedWidth],
      nice: true,
    });
  }, [data, categories, segments, boundedWidth]);

  // Calculate the stacked bar data
  const stackedData = useMemo(() => {
    return categories.map((category) => {
      let xOffset = 0;
      const segmentData = segments.map((segment) => {
        const value = getValue(data[category][segment] || 0);
        const barWidth = xScale(value);
        const startX = xOffset;
        xOffset += barWidth;
        return {
          segment,
          value,
          startX,
          width: barWidth,
        };
      });
      return {
        category,
        segments: segmentData,
        total: segmentData.reduce((sum, d) => sum + d.value, 0),
      };
    });
  }, [data, categories, segments, xScale]);

  // Format percentage for display
  const formatPercentage = (value, total) => {
    if (total === 0) return "0%";
    const percentage = (value / total) * 100;
    return `${percentage.toFixed(0)}%`;
  };

  return (
    <div>
      {title && <div className="chart-title">{title}</div>}
      {description && <div className="sr-only">{description}</div>}
      <div className="w-full overflow-x-auto">
        <div
          ref={containerRef}
          className={`horizontal-stacked-bar-chart ${className}`}
          style={{ position: "relative", minWidth: width }}
        >
          <svg
            width={width}
            height={height}
            role="img"
            aria-labelledby="chart-title chart-desc"
          >
            {title && (
              <title id="chart-title">
                {typeof title === "string"
                  ? title
                  : "Horizontal Stacked Bar Chart"}
              </title>
            )}
            {description && <desc id="chart-desc">{description}</desc>}
            <Group top={adjustedMargin.top} left={adjustedMargin.left}>
              {/* Render bars */}
              {stackedData.map(
                ({ category, segments: categorySegments, total }) => {
                  const y = yScale(category) || 0;
                  const barHeight = yScale.bandwidth();
                  return (
                    <Group key={category}>
                      {categorySegments.map(({ segment, startX, width }) => (
                        <Bar
                          key={`${category}-${segment}`}
                          x={startX}
                          y={y}
                          width={width}
                          height={barHeight}
                          fill={colorScale(segment)}
                        />
                      ))}
                      {/* Optional: Show percentages in the bars */}
                      {showPercentages &&
                        categorySegments.map(
                          ({ segment, startX, width, value }) => {
                            if (width < 40) return null;
                            return (
                              <Text
                                key={`text-${category}-${segment}`}
                                x={startX + width / 2}
                                y={y + barHeight / 2}
                                textAnchor="middle"
                                verticalAnchor="middle"
                                fill="#ffffff"
                                fontSize={12}
                                fontWeight={600}
                              >
                                {formatPercentage(value, total)}
                              </Text>
                            );
                          }
                        )}
                    </Group>
                  );
                }
              )}
              {/* Y Axis (categories) */}
              <AxisLeft
                scale={yScale}
                stroke="#333333"
                strokeWidth={3}
                tickLength={0}
                tickComponent={({ x, y, formattedValue }) => (
                  <Text
                    x={x - (adjustedMargin ? adjustedMargin.left * 0.1 : 15)}
                    y={y}
                    width={adjustedMargin ? adjustedMargin.left * 0.9 : 135}
                    textAnchor="end"
                    verticalAnchor="middle"
                    fill="#333333"
                    fontSize={16}
                    fontFamily="Overpass"
                    fontWeight={300}
                    lineHeight={"1.3em"}
                  >
                    {formattedValue}
                  </Text>
                )}
              />
              {/* Horizontal Legend */}
              {showLegend && (
                <Group top={boundedHeight + 20} left={0}>
                  <foreignObject width={boundedWidth} height={legendHeight}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        fontFamily: "Sofia Sans Condensed",
                        fontSize: 18,
                      }}
                    >
                      {segments.map((segment, i) => (
                        <div
                          key={segment}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            marginRight: i < segments.length - 1 ? 24 : 0,
                          }}
                        >
                          <svg
                            width={28}
                            height={28}
                            style={{ marginRight: 8 }}
                          >
                            <rect
                              x={4}
                              y={4}
                              width={20}
                              height={20}
                              fill={colorScale(segment)}
                            />
                          </svg>
                          <span>{segment}</span>
                        </div>
                      ))}
                    </div>
                  </foreignObject>
                </Group>
              )}
            </Group>
          </svg>
        </div>
      </div>
    </div>
  );
};

export default HorizontalStackedBarChart;
